import { useQuery } from '@powersync/react';

import { queries, type SidebarRitualRow, type SidebarViewRow } from '@flow/sync';

import { t } from '../i18n/es.js';

/**
 * Data of the sidebar (docs/05 §3): the sections come from the views and rituals the template
 * installed for this user, never from a list in the code. Counters are capped at
 * `COUNTER_CAP - 1` and shown as «99+» above it (design D2), so a local save never waits for a
 * count over the whole workspace.
 */

export const COUNTER_CAP = 100;

/** Which stage each countable view looks at; the views themselves arrive in their own changes. */
const COUNTED_BY_STAGE: Record<string, string> = {
  inbox: 'inbox',
  backlog: 'backlog',
  someday: 'someday',
};
/** The follow-up view counts what is overdue, not what is open (SEG-02). */
const OVERDUE_VIEW = 'waiting';
const OVERDUE_STAGE = 'waiting';
const FOLLOW_UP_FIELD = 'follow_up_on';

export interface SidebarData {
  views: SidebarViewRow[];
  rituals: SidebarRitualRow[];
  /** Formatted counter by view key («7», «99+»); missing when the view has no counter. */
  badges: Record<string, string>;
  /** View keys whose badge means «overdue», so it is not shown by colour alone. */
  warningViews: string[];
}

export function formatBadge(count: number): string | undefined {
  if (count <= 0) return undefined;
  return count >= COUNTER_CAP ? t.shell.badgeOverflow : String(count);
}

/**
 * Counter of a stage. When the stage is not there yet (the workspace is still syncing) it counts
 * an id that matches nothing: the hook is always called, and the query stays index-bounded.
 */
function useStageCount(stageId: string | undefined): number {
  const query = queries.openBlocksInStage(stageId ?? '');
  const { data } = useQuery<{ n: number }>(query.sql, query.params);
  return data[0]?.n ?? 0;
}

export function useSidebarData(userId: string, workspaceId: string): SidebarData {
  const viewsQuery = queries.sidebarViews(userId);
  const { data: views } = useQuery<SidebarViewRow>(viewsQuery.sql, viewsQuery.params);
  const ritualsQuery = queries.sidebarRituals(userId);
  const { data: rituals } = useQuery<SidebarRitualRow>(ritualsQuery.sql, ritualsQuery.params);

  const stagesQuery = queries.stages(workspaceId);
  const { data: stages } = useQuery<{ id: string; key: string }>(
    stagesQuery.sql,
    stagesQuery.params,
  );
  const stageId = (key: string) => stages.find((stage) => stage.key === key)?.id;

  const fieldQuery = queries.fieldDefByKey(workspaceId, FOLLOW_UP_FIELD);
  const { data: fields } = useQuery<{ id: string }>(fieldQuery.sql, fieldQuery.params);

  const inbox = useStageCount(stageId(COUNTED_BY_STAGE.inbox ?? ''));
  const backlog = useStageCount(stageId(COUNTED_BY_STAGE.backlog ?? ''));
  const someday = useStageCount(stageId(COUNTED_BY_STAGE.someday ?? ''));

  const overdueQuery = queries.overdueFollowUps(
    stageId(OVERDUE_STAGE) ?? '',
    fields[0]?.id ?? '',
    today(),
  );
  const { data: overdueRows } = useQuery<{ n: number }>(overdueQuery.sql, overdueQuery.params);
  const overdue = overdueRows[0]?.n ?? 0;

  const counts: Record<string, number> = { inbox, backlog, someday, [OVERDUE_VIEW]: overdue };
  const badges: Record<string, string> = {};
  for (const view of views) {
    const key = view.key;
    if (!key) continue;
    if (!(key in COUNTED_BY_STAGE) && key !== OVERDUE_VIEW) continue;
    const badge = formatBadge(counts[key] ?? 0);
    if (badge) badges[key] = badge;
  }

  return { views, rituals, badges, warningViews: [OVERDUE_VIEW] };
}

/** Local calendar date (`YYYY-MM-DD`), which is what `field_values.value_date` stores. */
function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}
